import {
  DEFAULT_PAGINATOR_SIZES,
  INSTALATION_STATUS,
  INSTANCE_TYPE,
  ROLE,
  WIDTH_NG,
} from "~/assets/js/constants";
import { connectionTimeToState, formatDate } from "~/assets/js/dateUtils";
import { createPaginator, isAllowedTo, vaccineInfoToStack } from "~/assets/js/utils";

export const toAgency = (agency) => ({
  id: agency.id,
  name: agency.name,
  hasLinux: agency.hasLinux,
  vaccineInfo: agency.vaccineInfo,
  totalComputers: agency.totalComputers,
  dates: agency.dates,
  relationship: agency.relationship,
  metadata: agency.metadata,
  instanceInfo: agency.instanceInfo,
  agentsConfig: agency.agentsConfig,
  externalConfig: agency.externalConfig,
});

export const toAgencyList = (agencies) => agencies.map(toAgency);

const getAgencyType = (instanceInfo) => {
  if (instanceInfo.instanceType === INSTANCE_TYPE.FEDERATED) return "federated";
  if (instanceInfo.instanceType === INSTANCE_TYPE.CLASSIFIED) return "classified";
  return "normal";
};

const setupAgencyActions = (role, i18n, type, isViewOnly) => {
  if (type === "classified") return [];

  if (isViewOnly || type === "federated") {
    return [
      {
        active: false,
        id: "agency-open",
        icon: "s2-icon-eye-regular",
        label: i18n.t("generic.open"),
        loading: false,
      },
    ];
  }

  const actions = [];

  if (isAllowedTo([ROLE.ADMIN, ROLE.SERVICEDESK, ROLE.MANAGER, ROLE.USER, ROLE.READONLY], role)) {
    actions.push({
      active: false,
      id: "agency-open",
      icon: "s2-icon-eye-regular",
      label: i18n.t("generic.open"),
      loading: false,
    });
  }

  if (isAllowedTo([ROLE.ADMIN, ROLE.SERVICEDESK, ROLE.MANAGER], role)) {
    actions.push({
      active: false,
      id: "agency-edit",
      icon: "s2-icon-pen-regular",
      label: i18n.t("generic.edit"),
      loading: false,
    });
  }

  if (isAllowedTo([ROLE.ADMIN, ROLE.SERVICEDESK], role)) {
    actions.push({
      active: false,
      id: "agency-delete",
      icon: "s2-icon-trash-regular",
      label: i18n.t("generic.delete"),
      loading: false,
    });
  }

  return actions;
};

export const toAgencyRow = (agency, options) => {
  const { role, i18n, viewOnly, showVersion = true } = options;
  const type = getAgencyType(agency.instanceInfo);
  const actions = setupAgencyActions(role, i18n, type, viewOnly);

  const columns = [
    { data: { label: agency.name }, id: "name", type: "text" },
    { data: { label: `${agency.metadata?.code}` }, id: "metadata.code", type: "text" },
    { data: { label: `${agency.totalComputers}` }, id: "totalComputers", type: "text" },
  ];

  if (showVersion) {
    columns.push({
      data: { label: `${agency.agentsConfig.version}` },
      id: "agentsConfig.version",
      type: "text",
    });
  }

  columns.push(
    {
      data: { stacks: vaccineInfoToStack(agency.vaccineInfo.windows, i18n) },
      id: "windowsState",
      type: "stack",
    },
    {
      data: {
        stacks: agency.hasLinux ? vaccineInfoToStack(agency.vaccineInfo.linux, i18n) : [],
      },
      id: "linuxState",
      type: "stack",
    },
    {
      data: {
        label: formatDate(i18n, agency.dates?.updatedOn),
        state: connectionTimeToState(agency.dates?.updatedOn),
      },
      id: "dates.updatedOn",
      type: "display",
    },
  );

  return {
    ...toAgency(agency),
    state: agency.metadata.status === INSTALATION_STATUS.CONTRACT_ENDED ? "disabled" : "ok",
    actions,
    columns,
  };
};

export const toAgencyTable = (agencies, options) => {
  const { role, i18n, viewOnly, showVersion = true } = options;
  const paginator = createPaginator(agencies, i18n, DEFAULT_PAGINATOR_SIZES);

  const sizes = [WIDTH_NG.LG, WIDTH_NG.MD, WIDTH_NG.SM];
  if (showVersion) sizes.push(WIDTH_NG.SM);
  sizes.push(WIDTH_NG.LG, WIDTH_NG.LG, 38);

  const headers = [
    { id: "name", label: i18n.t("agencies.headers.name"), sortable: true },
    { id: "metadata.code", label: i18n.t("agencies.headers.code"), sortable: true },
    { id: "totalComputers", label: i18n.t("agencies.headers.computers"), sortable: true },
  ];

  if (showVersion) {
    headers.push({
      id: "agentsConfig.version",
      label: i18n.t("agencies.headers.version"),
      sortable: true,
    });
  }

  headers.push(
    { id: "windowsState", label: i18n.t("agencies.headers.windowsState"), sortable: false },
    { id: "linuxState", label: i18n.t("agencies.headers.linuxState"), sortable: false },
    { id: "dates.updatedOn", label: i18n.t("agencies.headers.date"), sortable: true },
  );

  return {
    paginator,
    sizes,
    headers,
    main: "name",
    rows: agencies.collection.map((agency) =>
      toAgencyRow(agency, { role, i18n, viewOnly, showVersion }),
    ),
    sort: {
      default: "ascendant",
      id: agencies.sortBy ?? "name",
      loading: false,
      order: agencies.orderBy ?? "ascendant",
    },
  };
};

export const toAgencyOptions = (agencies, excludeId = null) =>
  agencies
    .filter((agency) => agency.id !== excludeId)
    .map((agency) => ({ id: agency.id, label: agency.name }));

export const toInstallationStatusOptions = (i18n) =>
  Object.entries(INSTALATION_STATUS)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      id: `${value}`,
      label: i18n.t(`agencies.edit.installationStatusTypes.${key}`),
    }));
